import lodash from 'lodash'
import HttpStatus from 'http-status-codes'
import HTTP from '@/utils/http'
import notification from '@/utils/notification'
import i18n from '@/utils/i18n'
import { sendEmail } from '@/api/postman/request'
import { EmailTypes } from '@/utils/consts'
import { PROJECT_TYPES } from '@/modules/projects/consts'
import { save } from '@/utils/xfs'

/**
 * Get tenders
 */
export async function getProjects (reqParam) {
  try {
    const response = await HTTP.get('projects', { params: { ...reqParam } })
    const projects = response.data.data
    return projects
  } catch (err) {
    notification.handleResponseError(err)
    return []
  }
}

export async function getVendorProjects (params) {
  try {
    const response = await HTTP.get('/projects/vendor?offset=0', { params })
    const projects = response.data.data
    return projects
  } catch (err) {
    if (deep(err, 'response.status') === HttpStatus.FORBIDDEN &&
      deep(err, 'response.data.message') === i18n.t('hint.permUpdatedHint')) {
      notification.warn(i18n.t('hint.relogin'))
    } else {
      notification.handleResponseError(err)
    }
    return []
  }
}

export async function getProject (projectId, isSilentGet) {
  try {
    const get = isSilentGet ? HTTP.silentGet : HTTP.get
    const response = await get(`projects/${projectId}`)
    const project = response.data.data
    if (!project) return null

    const submissions = lodash.orderBy(project.submissions, {'createdAt': 'desc'})
    project.submissions = submissions
    return project
  } catch (err) {
    notification.handleResponseError(err)
    return {}
  }
}

export async function createProject (request = {}) {
  try {
    const response = await HTTP.post('/projects', request)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err, 'po')
    return null
  }
}

export async function updateProject (id, request, action) {
  try {
    const actionHeaders = action ? { headers: { 'X-CMS-Action': action } } : undefined
    const response = await HTTP.put(`/projects/${id}`, request, actionHeaders)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function updateProjectStatus (id, status, action) {
  try {
    const response = await HTTP.patch(`/projects/${id}`, { status, action }, { headers: { 'X-CMS-Action': action } })
    return response
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function createSubmission (projectId) {
  try {
    const response = await HTTP.post('/submissions', { projectId })
    return response.data.data
  } catch (err) {
    if (deep(err, 'response.status') === HttpStatus.TOO_MANY_REQUESTS) {
      notification.fireNotification(false, i18n.t('error.createSubmission.429'))
    } else {
      notification.handleResponseError(err)
    }
    return null
  }
}

export async function updateSubmission (submission, showNotification = true) {
  try {
    const response = await HTTP.put(`/submissions/${submission.id}`, submission)
    if (showNotification) {
      notification.handleResponseAlert(response)
    }
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function updateSubmissionStatus (id, status, actionHistory, action, values) {
  try {
    const response = await HTTP
      .patch(`/submissions/${id}`,
        { ...values, status, actionHistory },
        { headers: { 'X-CMS-Action': action } },
      )
    notification.handleResponseAlert(response)
    return response
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function removeSubmission (id) {
  try {
    const response = await HTTP.delete(`/submissions/${id}`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return null
  }
}

export async function getSubmissions (params) {
  try {
    const response = await HTTP.get('/submissions', { params })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return []
  }
}

export async function getProjectMeta (numbers, isPo) {
  try {
    const response = await HTTP.get(`/projects/project-meta`, {
      params: {
        numbers: JSON.stringify(numbers),
        numberType: isPo ? PROJECT_TYPES.PO : PROJECT_TYPES.CONTRACT,
      },
    })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return {}
  }
}

export async function checkActiveProjectByNumber (poNumber, numberType, revisionNum) {
  try {
    const response = await HTTP.get(`/purchases/orders/${poNumber}/is-used`, {
      params: {
        numberType,
        revisionNum,
      },
    })
    const hasActiveProject = response.data.data
    return hasActiveProject
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function getLatestProjectByNumber (numberIds, numberType) {
  const query = {
    numberIds: JSON.stringify(numberIds),
    numberType,
  }
  try {
    const response = await HTTP.get('projects/by-numbers/latest', { params: query })
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return {}
  }
}

export async function notifySubmissionAcceptance (values) {
  const { order, action, unacceptedComment, showNote = false } = values
  const metadata = {
    order,
    action,
    showNote,
    unacceptedComment,
  }

  await sendEmail(
    EmailTypes.projects.submissionAcceptance,
    metadata,
  )
}

export async function listAllProjects (query, diskCacheKey) {
  try {
    const response = await HTTP
      .silent(false)
      .diskCache(diskCacheKey)
      .get('projects/all', { params: query })
    const result = response.data
    return [result.data, result.count]
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function updateRelatedPurchases (id, request) {
  try {
    const response = await HTTP.put(`projects/${id}/related`, request)
    const result = response.data.data
    return result
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function exportAllProjects (query) {
  const response = await HTTP.get('/projects/all/export', { params: query, responseType: 'arraybuffer' })
  save(response)
}

export async function getProjectInspector (id) {
  try {
    const response = await HTTP.get(`projects/${id}/inspector`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    return err
  }
}

export async function updateInspector (id, request) {
  try {
    const response = await HTTP.put(`/submissions/${id}/inspector`, request)
    notification.handleResponseAlert(response)
    return response
  } catch (err) {
    notification.handleResponseError(err)
    return err
  }
}

export async function getProjectAccounts (pid) {
  try {
    const response = await HTTP.get(`projects/${pid}/accounts`)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}

export async function upsertProjectAccounts (pid, request) {
  try {
    const response = await HTTP.put(`projects/${pid}/accounts`, request)
    return response.data.data
  } catch (err) {
    const isBadReq = lodash.get(err, 'response.status') === HttpStatus.BAD_REQUEST
    const errMsg = lodash.get(err, 'response.data.message')
    if (isBadReq && Array.isArray(errMsg)) {
      errMsg.forEach(msg => {
        notification.fireNotification(false, msg)
      })
    } else {
      notification.handleResponseError(err)
    }
    throw err
  }
}

export async function inviteProjectRep (pid, aid, request) {
  try {
    const response = await HTTP.post(`/projects/${pid}/accounts/${aid}/invite`, request)
    notification.handleResponseAlert(response)
    return response.data.data
  } catch (err) {
    notification.handleResponseError(err)
    throw err
  }
}
